import { Button, colors, Dialog, Divider, IconButton, InputAdornment, List, makeStyles, TextField, Theme, Typography, useMediaQuery, useTheme } from '@material-ui/core';
import React from 'react';
import CloseIcon from '@material-ui/icons/Close';
import SharedRepoOwnerDTO from 'dto/SharedRepoOwnerDTO';
import BriefUserInfoDTO from 'dto/BriefUserInfoDTO';
import DisableableTooltip from 'components/DisableableTooltip';
import Repository from 'services/repositories'
import AddedRepositoryUser from './AddedRepositoryUser';
import { LoadingRing } from 'components/Loading/Loading';
import AddRoundedIcon from '@material-ui/icons/AddRounded';
import useSnackbar from 'utils/useSnackbar';
import useConfirmation from 'utils/useConfirmation';
import { useSessionSelector } from 'reducers/sessionReducer';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    overflow: 'hidden'
  },
  header: {
    position: 'sticky',
    top: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(2)
  },
  main: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    overflow: 'auto',
    padding: theme.spacing(0, 2, 2)
  },
  actions: {
    position: 'sticky',
    bottom: 0,
    backgroundColor: colors.grey[100],
    padding: theme.spacing(2),
    display: 'flex',
    justifyContent: 'space-between'
  },
  input: {
    padding: 14
  },
  strongDivider: {
    background: '#D3D3D3'
  },
  Input: {
    marginBottom: theme.spacing(2)
  },
  FormHelper: {
    marginLeft: 0
  }
}))

interface ShareRepositoryDialogProps {
  open: boolean;
  onClose: () => void;
  handleRefresh: (reopen?: boolean) => void;
  repo: SharedRepoOwnerDTO
}

const ShareRepositoryDialog = (props: ShareRepositoryDialogProps) => {
  const {open, onClose, repo, handleRefresh} = props;
  const theme = useTheme() as Theme;
  const classes = useStyles();
  const fullScreen = useMediaQuery(theme.breakpoints.down('xs'));

  const { loginID } = useSessionSelector().user;

  const handleAlert = useSnackbar();
  const handleConfirmation = useConfirmation({
    title: 'Are you sure you want to unshare this repository'
  });

  const [toAdd, setToAdd] = React.useState<BriefUserInfoDTO[]>([]);
  const [loadingAdd, setLoadingAdd] = React.useState<boolean>(false);
  const [email, setEmail] = React.useState<string>('');
  const [emailError, setEmailError] = React.useState<string>('');
  

  const onShare = async () => {
    try {
      await Repository.shareRepo(repo.repoID, toAdd.map(u => u.loginID));
      handleRefresh(false);
      handleClose();
    } catch(err) {
      handleAlert(err);
    }
  }

  const onUnshare = async (user: BriefUserInfoDTO) => {
    await handleConfirmation();
    try {
      await Repository.unshareRepo(repo.repoID, user.loginID);
      handleAlert({message: `Repository unshared with ${user.firstName} ${user.lastName}`, severity: 'success', noBackdrop: true})
      handleRefresh(true);
    } catch(err) {
      handleAlert(err);
    }
  }
   

  const onEmailEnter = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if(e.key !== 'Enter') {
      return;
    }
    onSubmitEmail();
  }

  const handleClose = () => {
    onClose();
    setEmail('');
    setEmailError('');
    setToAdd([]);
    setLoadingAdd(false);
  }

  const onSubmitEmail = async () => {
    if(email.toLowerCase() === loginID.toLowerCase()) {
      setEmailError('You cannot share repositories with yourself');
      return;
    }

    const duplicateAdding = toAdd.find(e => e.loginID.toLowerCase() === email.toLowerCase());
    if(duplicateAdding) {
      setEmailError(`You are in the process of sharing this repository with ${duplicateAdding.firstName} ${duplicateAdding.lastName}. Click the Share button to begin sharing.`)
      return;
    }

    const duplicateAdded = repo.recepients.find(e => e.loginID.toLowerCase() === email.toLowerCase());
    if(duplicateAdded) {
      setEmailError(`You are already sharing this repository with ${duplicateAdded.firstName} ${duplicateAdded.lastName}`)
      return;
    }

    setLoadingAdd(true);
    let bui: BriefUserInfoDTO;
    try {
      bui = await Repository.getBriefUserInfo(email);
    } catch(e) {
      setEmailError(JSON.parse(e.request.responseText).toString())
      return;
    } finally {
      setLoadingAdd(false);
    }
    
    setEmailError('');
    setEmail('');
    setToAdd([...toAdd, bui])
  }

  return (
    <Dialog
      classes={{ paper: classes.root }}
      fullScreen={fullScreen}
      fullWidth
      maxWidth="xs"
      onClose={handleClose}
      open={open}
    >
      <div className={classes.header}>
        <Typography
          variant="h3"
        >
          Share Repository
        </Typography>
        <IconButton onClick={handleClose}>
          <CloseIcon />
        </IconButton>
      </div>
      <div className={classes.main}>
        <TextField
          FormHelperTextProps={{
            className: classes.FormHelper
          }}
          InputProps={{
            endAdornment: <InputAdornment position='end'>
              {loadingAdd ? <div style={{padding: 3}}><LoadingRing size={24}/></div>:<IconButton
                onClick={onSubmitEmail}
                size='small'
              >
                <AddRoundedIcon/>
              </IconButton>}
            </InputAdornment>
          }}
          className={classes.Input}
          error={!!emailError}
          fullWidth
          helperText={emailError}
          
          inputProps={{
            className: classes.input
          }}
          onChange={(e) => setEmail(e.target.value)}
          onKeyUp={onEmailEnter}
          placeholder='Email of user to add'
          type='email'
          value={email}
          variant='outlined'
        />

        {repo &&  !repo.recepients.length && !toAdd.length && <Typography 
          align='center' 
          variant='caption'
        >
          This repository is not shared with anyone
        </Typography>}
        <List>
          {toAdd.map(u => <AddedRepositoryUser 
            added 
            handleDelete={(u) => setToAdd(toAdd.filter(ua => ua.loginID !== u.loginID))}
            key={u.loginID}
            user={u} 
          />)}
          {toAdd.length && repo && repo.recepients.length && <Divider className={classes.strongDivider}/> || null}
          {repo && repo.recepients.map(u => <AddedRepositoryUser
            handleUnshare={onUnshare} 
            key={u.loginID}
            user={u} 
          />)}
        </List>
      </div>
      <div className={classes.actions}>
        <DisableableTooltip
          disabled={!!toAdd.length}
          title='You have not added any new people'
        >
          <Button
            color="primary"
            disabled={!toAdd.length}
            onClick={onShare}
            variant="contained"
          >
            Share
          </Button>
        </DisableableTooltip>
      </div>
    </Dialog>
  );
};

export default ShareRepositoryDialog;